'use client';

import { c as _c } from "react/compiler-runtime";
import { FOCUS_RING } from '@common/components/interaction';
import SvgChevronright from '@common/icon/SvgChevronright';
import SvgWarningFill from '@common/icon/SvgWarningFill';
import { cn } from '@core/utils';
import { type ReactNode, useState } from 'react';
import type { GiftTrialCampaign } from '@/api/getGiftTrialCampaign';
import type { GiftProductFetchResult } from '@/api/giftProductPublic';
import { CustomLink } from '@/app/_components/CustomLink';
import { ShareButton } from '@/app/donation/_components/ShareButton';
import { DesktopMainImages } from '@/app/donation/gifts/[productNumber]/_components/GiftDetailTemplate/HeaderInfoSection/DesktopMainImages';
import { MobileHero } from '@/app/donation/gifts/[productNumber]/_components/GiftDetailTemplate/HeaderInfoSection/MobileHero';
import { DELIVERY_PERIOD_LABEL } from '@/app/donation/gifts/[productNumber]/constants';
import { Rating } from '@/components/molecule/Rating';
import { DONATION_ROUTES } from '@/constants';
import { formatPrice } from '@/utils/formatPrice';
import { getCampaignPreviewSnapshot, matchCampaignPreview } from '../../_utils/campaignPreview';
import { CampaignInfoBlock } from './CampaignInfoBlock';
import { CampaignOptionPreview } from './CampaignOptionPreview';
import { GiftPreviewLink } from './GiftPreviewLink';
type GiftProduct = Extract<GiftProductFetchResult, {
  status: 'ok';
}>['product'];
export const CampaignSummary = t0 => {
  const $ = _c(74);
  const {
    campaign,
    giftProduct,
    recruitDayCount,
    children
  } = t0;
  let t1;
  if ($[0] !== campaign.campaignId) {
    t1 = () => matchCampaignPreview(getCampaignPreviewSnapshot(), campaign.campaignId)?.thumbnailSrc;
    $[0] = campaign.campaignId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [entryThumbnail] = useState(t1);
  let t2;
  if ($[2] !== giftProduct?.subImageUrlList) {
    t2 = giftProduct?.subImageUrlList ?? [];
    $[2] = giftProduct?.subImageUrlList;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== campaign.thumbnailUrl || $[5] !== t2) {
    t3 = [campaign.thumbnailUrl, ...t2];
    $[4] = campaign.thumbnailUrl;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const heroImageUrlList = t3;
  const institutionRouteId = giftProduct?.institutionSlugId ?? giftProduct?.institutionId;
  let t4;
  if ($[7] !== giftProduct?.deliveryExclusionRegionList) {
    t4 = (giftProduct?.deliveryExclusionRegionList ?? []).map(_temp).join(", ");
    $[7] = giftProduct?.deliveryExclusionRegionList;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const deliveryExclusionRegionNames = t4;
  const isExchangeBlocked = giftProduct?.exchangeYN === false;
  const institutionLabel = campaign.institutionName;
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = cn("grid grid-cols-2 gap-28", "mobile-tablet:grid-cols-1 mobile-tablet:gap-0");
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== entryThumbnail || $[11] !== heroImageUrlList) {
    t6 = <MobileHero heroImageUrlList={heroImageUrlList} adultYN={false} thumbnailSrc={entryThumbnail} />;
    $[10] = entryThumbnail;
    $[11] = heroImageUrlList;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== campaign.trialProductName || $[14] !== entryThumbnail || $[15] !== heroImageUrlList) {
    t7 = <DesktopMainImages title={campaign.trialProductName} heroImageUrlList={heroImageUrlList} adultYN={false} thumbnailSrc={entryThumbnail} />;
    $[13] = campaign.trialProductName;
    $[14] = entryThumbnail;
    $[15] = heroImageUrlList;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== campaign.institutionName || $[18] !== institutionLabel || $[19] !== institutionRouteId) {
    t8 = institutionRouteId ? <CustomLink href={DONATION_ROUTES.HOMETOWNS_DETAIL.pathname({
      id: institutionRouteId
    })} aria-label={`${campaign.institutionName} 상세 페이지로 이동`} className={cn("group flex w-fit items-center gap-2 rounded-xsmall font-600 text-Gray-600 underline-offset-2 typo-body3 mobile-tablet:font-400 mobile-tablet:typo-body4", "hover:text-Gray-900 hover:underline active:text-Gray-900 active:underline", FOCUS_RING)}>{institutionLabel}<SvgChevronright aria-hidden={true} className="size-16 shrink-0 transition-transform duration-150 group-hover:translate-x-1 group-focus-visible:translate-x-1 mobile-tablet:hidden motion-reduce:transition-none" /></CustomLink> : <p className="font-600 text-Gray-600 typo-body3 mobile-tablet:font-400 mobile-tablet:typo-body4">{institutionLabel}</p>;
    $[17] = campaign.institutionName;
    $[18] = institutionLabel;
    $[19] = institutionRouteId;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ShareButton />;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t8) {
    t10 = <div className="flex items-center justify-between gap-8">{t8}{t9}</div>;
    $[22] = t8;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== campaign.trialProductName) {
    t11 = <h2 className="font-700 text-Gray-900 typo-h3 mobile-tablet:font-600 mobile-tablet:text-Gray-black mobile-tablet:typo-body1">{campaign.trialProductName}</h2>;
    $[24] = campaign.trialProductName;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== campaign.institutionName || $[27] !== campaign.productName || $[28] !== campaign.productNumber || $[29] !== giftProduct) {
    t12 = giftProduct && giftProduct.reviewCount > 0 ? <GiftPreviewLink preview={{
      productNumber: campaign.productNumber,
      mainImageUrl: giftProduct.mainImageUrl ?? undefined,
      productName: campaign.productName,
      institutionName: campaign.institutionName
    }} href={DONATION_ROUTES.GIFTS_DETAIL.withSearchParams({
      dynamicPath: {
        productNumber: campaign.productNumber
      },
      searchParams: {
        tab: "review"
      }
    })} className={cn("group flex w-fit items-center gap-8 rounded-xsmall mobile-tablet:hidden", FOCUS_RING)}><Rating value={giftProduct.reviewRating ?? 0} size={24} /><span className="font-600 text-Gray-600 underline underline-offset-2 transition-colors typo-body3 group-hover:text-Gray-900 group-active:text-Gray-900">{giftProduct.reviewCount.toLocaleString()}건 후기 보기</span></GiftPreviewLink> : null;
    $[26] = campaign.institutionName;
    $[27] = campaign.productName;
    $[28] = campaign.productNumber;
    $[29] = giftProduct;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== t11 || $[32] !== t12) {
    t13 = <div className="flex flex-col gap-12">{t11}{t12}</div>;
    $[31] = t11;
    $[32] = t12;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== t10 || $[35] !== t13) {
    t14 = <div className="flex flex-col gap-8">{t10}{t13}</div>;
    $[34] = t10;
    $[35] = t13;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== campaign.marketPrice) {
    t15 = formatPrice(campaign.marketPrice);
    $[37] = campaign.marketPrice;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t15) {
    t16 = <s className="font-700 text-Gray-550 typo-body1 mobile-tablet:font-600 mobile-tablet:typo-body3">{t15}</s>;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <p className="flex items-center gap-8 font-600 text-O-400 typo-body3 mobile-tablet:items-end mobile-tablet:gap-4 mobile-tablet:typo-body4"><strong className="font-700 typo-h3 mobile-tablet:typo-h5">0원</strong><span className="mobile-tablet:pb-2">체험단 선정 시</span></p>;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== t16) {
    t18 = <div className="flex flex-col">{t16}{t17}</div>;
    $[42] = t16;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== t14 || $[45] !== t18) {
    t19 = <div className="flex flex-col gap-28 mobile-tablet:gap-16 mobile-tablet:px-24">{t14}{t18}</div>;
    $[44] = t14;
    $[45] = t18;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== campaign || $[48] !== giftProduct || $[49] !== recruitDayCount) {
    t20 = <div className="mobile-tablet:px-24"><CampaignInfoBlock campaign={campaign} recruitDayCount={recruitDayCount} gift={giftProduct} /></div>;
    $[47] = campaign;
    $[48] = giftProduct;
    $[49] = recruitDayCount;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <div className="flex gap-56 mobile-tablet:gap-16"><dt className="w-92 shrink-0 font-500 text-Gray-600 mobile-tablet:font-400">배송<span className="mobile-tablet:hidden"> </span>정보</dt><dd className="font-500 text-Gray-550 mobile-tablet:font-400 mobile-tablet:text-Gray-900">무료배송</dd></div>;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  let t22;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <dt className="w-92 shrink-0 font-500 text-Gray-600 mobile-tablet:font-400">배송<span className="mobile-tablet:hidden"> </span>안내</dt>;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <dl className="flex flex-col gap-16 mobile-tablet:gap-12">{t21}<div className="flex gap-56 mobile-tablet:gap-16">{t22}<dd className="flex gap-4 font-500 text-Gray-550 mobile-tablet:flex-col mobile-tablet:font-400 mobile-tablet:text-Gray-900">{DELIVERY_PERIOD_LABEL}<span className="font-500">(식품의 경우 지연 발생 시 1주~2주 소요)</span></dd></div></dl>;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== deliveryExclusionRegionNames || $[55] !== isExchangeBlocked) {
    t24 = isExchangeBlocked || deliveryExclusionRegionNames ? <ul className="flex flex-col gap-8 rounded-xsmall border border-Gray-300 px-20 py-12 text-Gray-600 typo-caption1">{isExchangeBlocked ? <li className="flex items-center gap-4"><SvgWarningFill aria-hidden={true} className="size-13 shrink-0 text-B-500" />해당 답례품은 반품/교환이 불가해요.</li> : null}{deliveryExclusionRegionNames ? <li className="flex items-start gap-4"><SvgWarningFill aria-hidden={true} className="mt-3 size-13 shrink-0 text-B-500" /><span className="shrink-0">배송불가 지역</span><span aria-hidden={true} className="text-Gray-500 typo-caption2">|</span><span className="text-Gray-900">{deliveryExclusionRegionNames}</span></li> : null}</ul> : null;
    $[54] = deliveryExclusionRegionNames;
    $[55] = isExchangeBlocked;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] !== campaign.campaignPhase || $[58] !== campaign.optionNames) {
    t25 = campaign.campaignPhase === "RECRUITING" ? null : <CampaignOptionPreview optionNames={campaign.optionNames} />;
    $[57] = campaign.campaignPhase;
    $[58] = campaign.optionNames;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== t24 || $[61] !== t25) {
    t26 = <div className="flex flex-col gap-24 border-t border-Gray-200 pt-20 typo-body3 mobile-tablet:gap-16 mobile-tablet:p-20 mobile-tablet:typo-body4">{t23}{t24}{t25}</div>;
    $[60] = t24;
    $[61] = t25;
    $[62] = t26;
  } else {
    t26 = $[62];
  }
  let t27;
  if ($[63] !== children) {
    t27 = <div className="pt-16 mobile-tablet:hidden">{children}</div>;
    $[63] = children;
    $[64] = t27;
  } else {
    t27 = $[64];
  }
  let t28;
  if ($[65] !== t19 || $[66] !== t20 || $[67] !== t26 || $[68] !== t27) {
    t28 = <div className="flex min-w-0 flex-col gap-20 break-keep px-20 mobile-tablet:relative mobile-tablet:z-[1] mobile-tablet:-mt-20 mobile-tablet:rounded-t-large mobile-tablet:bg-Gray-white mobile-tablet:px-0 mobile-tablet:pt-24">{t19}{t20}{t26}{t27}</div>;
    $[65] = t19;
    $[66] = t20;
    $[67] = t26;
    $[68] = t27;
    $[69] = t28;
  } else {
    t28 = $[69];
  }
  let t29;
  if ($[70] !== t28 || $[71] !== t6 || $[72] !== t7) {
    t29 = <div className={t5}>{t6}{t7}{t28}</div>;
    $[70] = t28;
    $[71] = t6;
    $[72] = t7;
    $[73] = t29;
  } else {
    t29 = $[73];
  }
  return t29;
};
function _temp(region) {
  return region.regionName;
}